import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../../hooks/useAuth'
import { getGuests, createGuest, updateGuest, deleteGuest, getGuestStats } from '../../../services/guestsService'
import { getOrCreateRSVPInvite } from '../../../services/rsvpInviteService'
import { showAlert, showConfirm } from '../../../services/dialogService'
import { supabase } from '../../../config/supabase'
import InfoButton from '../../../components/InfoButton/InfoButton'
import Loader from '../../../components/Loader/Loader'
import TextareaWithCounter from '../../../components/TextareaWithCounter/TextareaWithCounter'
import '../Section.css'
import './GuestManagerIndex.css'

function GuestManagerIndex() {
  const { user, loading: authLoading } = useAuth()
  const [guests, setGuests] = useState([])
  const [stats, setStats] = useState({
    all: 0,
    confirmed: 0,
    declined: 0,
    pending: 0,
    not_invited: 0,
  })
  const [activeTab, setActiveTab] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [filterBy, setFilterBy] = useState('')
  const [sortBy, setSortBy] = useState('first_name')
  const [sortOrder, setSortOrder] = useState('asc')
  const [showSettings, setShowSettings] = useState(false)
  const [showAddGuest, setShowAddGuest] = useState(false)
  const [selectedGuests, setSelectedGuests] = useState([])
  const [loading, setLoading] = useState(true)
  const [isAddingGuest, setIsAddingGuest] = useState(false)
  const [isUpdatingGuest, setIsUpdatingGuest] = useState(false)
  const [editingGuest, setEditingGuest] = useState(null)
  const [editGuestData, setEditGuestData] = useState({})
  const [rsvpFormQuestions, setRsvpFormQuestions] = useState([])

  // Preguntas custom del RSVP (las que el usuario añade en Craft Your Invite) para mostrar columnas dinámicas
  const customRsvpQuestions = useMemo(
    () => (rsvpFormQuestions || []).filter((q) => ['custom_text', 'custom_textarea', 'custom_choice'].includes(q.type)),
    [rsvpFormQuestions]
  )

  // Formatear valor de custom_rsvp_answers para mostrar en tabla
  const formatCustomAnswer = (value) => {
    if (value == null || value === '') return '—'
    if (Array.isArray(value)) return value.filter(Boolean).join(', ') || '—'
    return String(value)
  }

  // Column header for custom questions: use columnLabel (from Craft Your Invite) if set, else question label; truncate if very long
  const MAX_COLUMN_LABEL_LENGTH = 28
  const getCustomColumnLabel = (q) => {
    const text = (q.columnLabel && String(q.columnLabel).trim()) ? String(q.columnLabel).trim() : (q.label || q.id || '')
    if (text.length <= MAX_COLUMN_LABEL_LENGTH) return text
    return text.slice(0, MAX_COLUMN_LABEL_LENGTH).trim() + '…'
  }
  const getCustomColumnTitle = (q) => (q.label || q.id || '')

  // Column visibility settings - Cargar desde localStorage o usar valores por defecto
  const loadColumnSettings = () => {
    try {
      const saved = localStorage.getItem('guestManager_visibleColumns')
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (error) {
      console.warn('Error loading column settings from localStorage:', error)
    }
    // Valores por defecto
    return {
      first_name: true,
      last_name: true,
      plus_one: true,
      rsvp: true,
      email: true,
      list: true,
      group: true,
      dietary: true,
      allergies: true,
      phone: false,
      stationery: false,
      side: false,
      age: false,
      notes: true,
      table_number: true,
      position: true,
    }
  }

  const [visibleColumns, setVisibleColumns] = useState(loadColumnSettings())

  // Añadir columnas de preguntas custom al cargar (respeta lo guardado en localStorage)
  const customQuestionIds = customRsvpQuestions.map((q) => q.id).join(',')
  useEffect(() => {
    if (customRsvpQuestions.length === 0) return
    setVisibleColumns((prev) => ({
      ...prev,
      ...Object.fromEntries(customRsvpQuestions.map((q) => [`custom_${q.id}`, prev[`custom_${q.id}`] ?? true])),
    }))
  }, [customQuestionIds, customRsvpQuestions])

  // New guest form
  const [newGuest, setNewGuest] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    rsvp_status: 'pending',
    has_plus_one: false,
    plus_one_first_name: '',
    plus_one_last_name: '',
    plus_one_email: '',
    plus_one_phone: '',
    dietary_requirements: '',
    allergies: '',
    guest_list: '',
    guest_group: '',
    table_number: '',
    position: '',
    notes: '',
  })

  // Load guests
  useEffect(() => {
    if (!user) return

    const loadGuests = async () => {
      try {
        setLoading(true)
        const [guestsData, inviteData] = await Promise.all([
          getGuests(user.id),
          getOrCreateRSVPInvite(user.id).catch(() => null),
        ])
        if (inviteData?.form_questions && Array.isArray(inviteData.form_questions)) {
          setRsvpFormQuestions(inviteData.form_questions)
        } else {
          setRsvpFormQuestions([])
        }
        
        // Enriquecer los datos con información de +1 relacionados
        const enrichedGuests = guestsData.map(guest => {
          // Si tiene +1, buscar el invitado relacionado
          if (guest.has_plus_one) {
            const plusOneGuest = guestsData.find(g => g.guest_of_id === guest.id)
            if (plusOneGuest) {
              guest.plus_one_info = {
                id: plusOneGuest.id,
                first_name: plusOneGuest.first_name,
                last_name: plusOneGuest.last_name,
                full_name: plusOneGuest.full_name,
                email: plusOneGuest.email,
                phone: plusOneGuest.phone
              }
            }
          }
          
          // Si este invitado ES un +1, buscar el invitado principal
          if (guest.guest_of_id) {
            const mainGuest = guestsData.find(g => g.id === guest.guest_of_id)
            if (mainGuest) {
              guest.main_guest_info = {
                id: mainGuest.id,
                first_name: mainGuest.first_name,
                last_name: mainGuest.last_name,
                full_name: mainGuest.full_name
              }
            }
          }
          
          return guest
        })
        
        const statsData = await getGuestStats(user.id)
        setGuests(enrichedGuests)
        setStats(statsData)
      } catch (error) {
        console.error('Error loading guests:', error)
      } finally {
        // Reducido delay para mejor rendimiento
        setTimeout(() => {
          setLoading(false)
        }, 50)
      }
    }

    loadGuests()
  }, [user])

  // Filter and sort guests - optimizado con useMemo para evitar recálculos innecesarios
  const filteredGuests = useMemo(() => {
    let filtered = [...guests]

    // Filter by tab
    if (activeTab === 'confirmed') {
      filtered = filtered.filter((g) => g.rsvp_status === 'confirmed')
    } else if (activeTab === 'declined') {
      filtered = filtered.filter((g) => g.rsvp_status === 'declined')
    } else if (activeTab === 'pending') {
      filtered = filtered.filter((g) => g.rsvp_status === 'pending')
    } else if (activeTab === 'not_invited') {
      filtered = filtered.filter((g) => !g.rsvp_status || g.rsvp_status === 'not_invited')
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (g) =>
          g.first_name?.toLowerCase().includes(query) ||
          g.last_name?.toLowerCase().includes(query) ||
          g.email?.toLowerCase().includes(query) ||
          g.guest_group?.toLowerCase().includes(query)
      )
    }

    // Filter by additional filter
    if (filterBy) {
      if (filterBy === 'has_plus_one') {
        // Mostrar solo invitados principales que tienen +1 (no los +1 mismos)
        filtered = filtered.filter((g) => g.has_plus_one === true && !g.guest_of_id)
      } else if (filterBy === 'no_plus_one') {
        // Mostrar invitados que no tienen +1 (incluyendo los +1 mismos)
        filtered = filtered.filter((g) => !g.has_plus_one)
      } else if (filterBy.startsWith('group_')) {
        const group = filterBy.replace('group_', '')
        filtered = filtered.filter((g) => g.guest_group === group)
      } else if (filterBy.startsWith('list_')) {
        const list = filterBy.replace('list_', '')
        filtered = filtered.filter((g) => g.guest_list === list)
      }
    }

    // Sort - crear copia antes de ordenar para no mutar el array original
    const sorted = [...filtered].sort((a, b) => {
      let aVal, bVal

      if (sortBy === 'first_name') {
        aVal = a.first_name || ''
        bVal = b.first_name || ''
      } else if (sortBy === 'last_name') {
        aVal = a.last_name || ''
        bVal = b.last_name || ''
      } else if (sortBy === 'group') {
        aVal = a.guest_group || ''
        bVal = b.guest_group || ''
      } else if (sortBy === 'dietary') {
        aVal = a.dietary_requirements || ''
        bVal = b.dietary_requirements || ''
      } else if (sortBy === 'allergies') {
        aVal = a.allergies || ''
        bVal = b.allergies || ''
      } else {
        aVal = a[sortBy] || ''
        bVal = b[sortBy] || ''
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
      }
    })

    return sorted
  }, [guests, activeTab, searchQuery, filterBy, sortBy, sortOrder])

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
  }

  const handleAddGuest = async (e) => {
    e.preventDefault()
    
    // Prevenir múltiples envíos
    if (isAddingGuest) {
      return
    }
    
    try {
      setIsAddingGuest(true)
      
      // Validar campos requeridos
      if (!newGuest.first_name.trim() || !newGuest.last_name.trim()) {
        await showAlert('First name and last name are required.')
        setIsAddingGuest(false)
        return
      }

      // Si tiene +1, validar que tenga nombre y apellido
      if (newGuest.has_plus_one) {
        if (!newGuest.plus_one_first_name?.trim() || !newGuest.plus_one_last_name?.trim()) {
          await showAlert('Plus one first name and last name are required when "Has +1" is checked.')
          setIsAddingGuest(false)
          return
        }
      }

      const created = await createGuest(user.id, newGuest)
      // Recargar la lista completa para incluir el +1 si se creó
      const updatedGuests = await getGuests(user.id)
      
      // Enriquecer los datos con información de +1 relacionados
      const enrichedGuests = updatedGuests.map(guest => {
        // Si tiene +1, buscar el invitado relacionado
        if (guest.has_plus_one) {
          const plusOneGuest = updatedGuests.find(g => g.guest_of_id === guest.id)
          if (plusOneGuest) {
            guest.plus_one_info = {
              id: plusOneGuest.id,
              first_name: plusOneGuest.first_name,
              last_name: plusOneGuest.last_name,
              full_name: plusOneGuest.full_name,
              email: plusOneGuest.email,
              phone: plusOneGuest.phone
            }
          }
        }
        
        // Si este invitado ES un +1, buscar el invitado principal
        if (guest.guest_of_id) {
          const mainGuest = updatedGuests.find(g => g.id === guest.guest_of_id)
          if (mainGuest) {
            guest.main_guest_info = {
              id: mainGuest.id,
              first_name: mainGuest.first_name,
              last_name: mainGuest.last_name,
              full_name: mainGuest.full_name
            }
          }
        }
        
        return guest
      })
      
      setGuests(enrichedGuests)
      const statsData = await getGuestStats(user.id)
      setStats(statsData)
      setShowAddGuest(false)
      setNewGuest({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        rsvp_status: 'pending',
        has_plus_one: false,
        plus_one_first_name: '',
        plus_one_last_name: '',
        plus_one_email: '',
        plus_one_phone: '',
        dietary_requirements: '',
        guest_list: '',
        guest_group: '',
        table_number: '',
        position: '',
        notes: '',
      })
      await showAlert('Guest added successfully!')
    } catch (error) {
      console.error('Error adding guest:', error)
      // Mostrar mensaje de error más específico
      let errorMessage = 'Error adding guest. Please try again.'
      
      if (error.message) {
        if (error.message.includes('No wedding found')) {
          errorMessage = 'Error: No wedding found. Please make sure you have created a wedding first.'
        } else if (error.message.includes('duplicate') || error.message.includes('unique')) {
          errorMessage = 'Error: A guest with this email already exists.'
        } else if (error.message.includes('violates row-level security')) {
          errorMessage = 'Error: Permission denied. Please check your database policies.'
        } else {
          errorMessage = `Error: ${error.message}`
        }
      } else if (error.code) {
        errorMessage = `Error ${error.code}: ${error.message || 'Please try again.'}`
      }
      
      await showAlert(errorMessage)
    } finally {
      setIsAddingGuest(false)
    }
  }

  const handleDeleteGuest = async (guestId) => {
    const ok = await showConfirm('Are you sure you want to delete this guest?', { title: 'Delete guest', confirmLabel: 'Delete', cancelLabel: 'Cancel', variant: 'danger' })
    if (!ok) return

    try {
      await deleteGuest(guestId)
      setGuests(guests.filter((g) => g.id !== guestId))
      const statsData = await getGuestStats(user.id)
      setStats(statsData)
    } catch (error) {
      console.error('Error deleting guest:', error)
      await showAlert('Error deleting guest. Please try again.')
    }
  }

  const handleEditGuest = async (guest) => {
    setEditingGuest(guest.id)
    
    // IMPORTANTE: Si este invitado ES un +1 (tiene guest_of_id), NO buscar otro +1
    // Los +1 no pueden tener sus propios +1
    const isPlusOne = !!guest.guest_of_id
    
    // Buscar si existe un +1 para este guest usando la relación guest_of_id
    // Solo si NO es un +1 y tiene has_plus_one = true
    let plusOneData = {
      plus_one_first_name: '',
      plus_one_last_name: '',
      plus_one_email: '',
      plus_one_phone: '',
    }
    
    if (!isPlusOne && guest.has_plus_one) {
      try {
        // Obtener todos los guests para buscar el +1 usando guest_of_id
        const allGuests = await getGuests(user.id)
        
        // Buscar el +1 usando la relación correcta: guest_of_id apunta al invitado principal
        const plusOneGuest = allGuests.find(g => g.guest_of_id === guest.id)
        
        if (plusOneGuest) {
          plusOneData = {
            plus_one_first_name: plusOneGuest.first_name || '',
            plus_one_last_name: plusOneGuest.last_name || '',
            plus_one_email: plusOneGuest.email || '',
            plus_one_phone: plusOneGuest.phone || '',
          }
        }
      } catch (error) {
        console.error('Error finding plus one:', error)
        // Si hay error, dejar los campos vacíos
      }
    }
    
    // IMPORTANTE: Cada invitado tiene sus propios datos individuales
    // No mezclar datos entre el principal y el +1
    // Si es un +1, has_plus_one debe ser false (los +1 no pueden tener +1)
    setEditGuestData({
      first_name: guest.first_name || '',
      last_name: guest.last_name || '',
      email: guest.email || '',
      phone: guest.phone || '',
      rsvp_status: guest.rsvp_status || 'pending',
      has_plus_one: isPlusOne ? false : (guest.has_plus_one || false), // Los +1 no pueden tener +1
      ...plusOneData,
      // Cada campo es independiente y específico de este invitado
      dietary_requirements: guest.dietary_requirements || '',
      allergies: guest.allergies || '',
      guest_list: guest.guest_list || '',
      guest_group: guest.guest_group || '',
      table_number: guest.table_number || '',
      position: guest.position || '',
      notes: guest.notes || '',
    })
  }

  const handleSaveEditGuest = async (e) => {
    e.preventDefault()
    if (!editingGuest) return
    
    // Prevenir múltiples envíos
    if (isUpdatingGuest) {
      return
    }

    try {
      setIsUpdatingGuest(true)
      // Preparar los datos del invitado (similar a createGuest)
      const firstName = (editGuestData.first_name || '').trim()
      const lastName = (editGuestData.last_name || '').trim()
      
      // Validar que first_name y last_name no estén vacíos
      if (!firstName || !lastName) {
        await showAlert('First name and last name are required.')
        setIsUpdatingGuest(false)
        return
      }

      // Si tiene +1, validar que tenga nombre y apellido
      if (editGuestData.has_plus_one) {
        if (!editGuestData.plus_one_first_name?.trim() || !editGuestData.plus_one_last_name?.trim()) {
          await showAlert('Plus one first name and last name are required when "Has +1" is checked.')
          setIsUpdatingGuest(false)
          return
        }
      }

      // Crear full_name concatenando first_name y last_name
      const fullName = `${firstName} ${lastName}`.trim()

      // Preparar los datos de actualización
      const updateData = {
        first_name: firstName,
        last_name: lastName,
        full_name: fullName,
        email: editGuestData.email?.trim() || null,
        phone: editGuestData.phone?.trim() || null,
        rsvp_status: editGuestData.rsvp_status || 'pending',
        has_plus_one: editGuestData.has_plus_one || false,
        dietary_requirements: editGuestData.dietary_requirements?.trim() || null,
        allergies: editGuestData.allergies?.trim() || null,
        guest_list: editGuestData.guest_list?.trim() || null,
        guest_group: editGuestData.guest_group?.trim() || null,
        position: editGuestData.position?.trim() || null,
        notes: editGuestData.notes?.trim() || null,
      }

      // Convertir table_number a número si existe, sino null
      if (editGuestData.table_number) {
        const tableNum = parseInt(editGuestData.table_number, 10)
        updateData.table_number = !isNaN(tableNum) ? tableNum : null
      } else {
        updateData.table_number = null
      }
      
      await updateGuest(editingGuest, updateData)
      
      // Verificar si el invitado que se está editando ES un +1
      // Los +1 no pueden tener sus propios +1
      const allGuestsBeforeUpdate = await getGuests(user.id)
      const currentGuestBeforeUpdate = allGuestsBeforeUpdate.find(g => g.id === editingGuest)
      const isEditingAPlusOne = !!currentGuestBeforeUpdate?.guest_of_id
      
      // Si tiene +1, buscar si ya existe o crear uno nuevo
      // IMPORTANTE: Solo si NO es un +1 (los +1 no pueden tener +1)
      if (!isEditingAPlusOne && editGuestData.has_plus_one && editGuestData.plus_one_first_name?.trim() && editGuestData.plus_one_last_name?.trim()) {
        const plusOneFirstName = editGuestData.plus_one_first_name.trim()
        const plusOneLastName = editGuestData.plus_one_last_name.trim()
        const plusOneFullName = `${plusOneFirstName} ${plusOneLastName}`.trim()

        try {
          // Obtener el wedding_id del guest actual (ya lo tenemos de antes)
          const weddingId = currentGuestBeforeUpdate?.wedding_id
          
          if (!weddingId) {
            throw new Error('No wedding found')
          }

          // Buscar si ya existe un +1 para este invitado usando guest_of_id
          // Esto es más preciso que buscar solo por nombre
          const { data: existingPlusOne } = await supabase
            .from('guests')
            .select('*')
            .eq('wedding_id', weddingId)
            .eq('guest_of_id', editingGuest) // Buscar por la relación correcta
            .maybeSingle()
          
          // Si no se encuentra por guest_of_id, buscar por nombre como fallback
          // (para casos donde el +1 existe pero no tiene guest_of_id configurado)
          let plusOneToUpdate = existingPlusOne
          if (!plusOneToUpdate) {
            const { data: byName } = await supabase
              .from('guests')
              .select('*')
              .eq('wedding_id', weddingId)
              .eq('first_name', plusOneFirstName)
              .eq('last_name', plusOneLastName)
              .neq('id', editingGuest)
              .maybeSingle()
            plusOneToUpdate = byName
          }

          // IMPORTANTE: Solo actualizar datos básicos del +1
          // NO copiar dietary, allergies, table_number, position, notes del invitado principal
          // Esos campos se editan individualmente desde el +1
          const plusOneUpdateData = {
            first_name: plusOneFirstName,
            last_name: plusOneLastName,
            full_name: plusOneFullName,
            email: editGuestData.plus_one_email?.trim() || null,
            phone: editGuestData.plus_one_phone?.trim() || null,
            has_plus_one: false,
            // Mantener el mismo grupo/lista que el principal (pero NO dietary, table, position, notes)
            guest_list: editGuestData.guest_list?.trim() || null,
            guest_group: editGuestData.guest_group?.trim() || null,
          }

          if (plusOneToUpdate) {
            // Si existe, actualizarlo solo con los datos básicos
            // NO sobrescribir dietary, table, position, notes, rsvp_status del +1
            await updateGuest(plusOneToUpdate.id, plusOneUpdateData)
          } else {
            // Si no existe, crearlo con guest_of_id para establecer la relación
            plusOneUpdateData.guest_of_id = editingGuest
            plusOneUpdateData.rsvp_status = 'pending' // Nuevo +1 empieza en pending
            await createGuest(user.id, plusOneUpdateData)
          }
        } catch (plusOneError) {
          console.error('Error handling plus one:', plusOneError)
          // No lanzar error, solo loguear para que el guest principal se actualice igual
        }
      } else if (!editGuestData.has_plus_one) {
        // Si se desmarcó has_plus_one, no hacer nada (el +1 existente se mantiene como guest separado)
        // Esto es intencional - el +1 sigue siendo un guest válido aunque no esté marcado como +1
      }
      
      const updatedGuests = await getGuests(user.id)
      
      // Enriquecer los datos con información de +1 relacionados
      const enrichedGuests = updatedGuests.map(guest => {
        // Si tiene +1, buscar el invitado relacionado
        if (guest.has_plus_one) {
          const plusOneGuest = updatedGuests.find(g => g.guest_of_id === guest.id)
          if (plusOneGuest) {
            guest.plus_one_info = {
              id: plusOneGuest.id,
              first_name: plusOneGuest.first_name,
              last_name: plusOneGuest.last_name,
              full_name: plusOneGuest.full_name,
              email: plusOneGuest.email,
              phone: plusOneGuest.phone
            }
          }
        }
        
        // Si este invitado ES un +1, buscar el invitado principal
        if (guest.guest_of_id) {
          const mainGuest = updatedGuests.find(g => g.id === guest.guest_of_id)
          if (mainGuest) {
            guest.main_guest_info = {
              id: mainGuest.id,
              first_name: mainGuest.first_name,
              last_name: mainGuest.last_name,
              full_name: mainGuest.full_name
            }
          }
        }
        
        return guest
      })
      
      const updatedStats = await getGuestStats(user.id)
      setGuests(enrichedGuests)
      setStats(updatedStats)
      setEditingGuest(null)
      setEditGuestData({})
      await showAlert('Guest updated successfully!')
    } catch (error) {
      console.error('Error updating guest:', error)
      await showAlert(`Error updating guest: ${error.message || 'Please try again.'}`)
    } finally {
      setIsUpdatingGuest(false)
    }
  }


  const handleToggleColumn = (column) => {
    const newColumns = { ...visibleColumns, [column]: !visibleColumns[column] }
    setVisibleColumns(newColumns)
    // Guardar automáticamente en localStorage cuando se cambia
    try {
      localStorage.setItem('guestManager_visibleColumns', JSON.stringify(newColumns))
    } catch (error) {
      console.warn('Error saving column settings to localStorage:', error)
    }
  }

  const handleSaveColumnSettings = async () => {
    try {
      localStorage.setItem('guestManager_visibleColumns', JSON.stringify(visibleColumns))
      await showAlert('Column settings saved successfully!')
      setShowSettings(false)
    } catch (error) {
      console.error('Error saving column settings:', error)
      await showAlert('Error saving settings. Please try again.')
    }
  }

  const getRSVPStatusDisplay = (status) => {
    if (!status || status === 'not_invited') return { text: 'Not invited yet', color: '#999' }
    if (status === 'confirmed') return { text: 'Confirmed', color: '#4CAF50' }
    if (status === 'declined') return { text: 'Regretfully declined', color: '#F44336' }
    return { text: 'Waiting for reply', color: '#FF9800' }
  }

  const getUniqueGroups = () => {
    const groups = [...new Set(guests.map((g) => g.guest_group).filter(Boolean))]
    return groups
  }

  const getUniqueLists = () => {
    const lists = [...new Set(guests.map((g) => g.guest_list).filter(Boolean))]
    return lists
  }

  // Mostrar loader mientras se cargan los datos o mientras se verifica la autenticación
  if (authLoading || loading) {
    return <Loader />
  }

  return (
    <div className="dashboard-section guest-manager">
      <h1>
        GUEST MANAGER
        <InfoButton
          title="Guest Manager"
          content={
            <div>
              <p>
                Your Guest Hub — Every guest in one place. Keep all their info, RSVPs, dietary needs, +1s, and table assignments organized and up-to-date. No spreadsheets, no chaos — just clarity.
              </p>
              <p>
                <strong>Features:</strong>
              </p>
              <ul>
                <li><strong>Guest list:</strong> Build your complete guest list</li>
                <li><strong>The Lifesaver Link:</strong> Share a link for guests to add themselves with customizable form fields</li>
                <li><strong>Send an invite:</strong> Design and send beautiful invitations</li>
                <li><strong>RSVP Reminder:</strong> Send reminders to guests who haven't RSVP'd</li>
                <li><strong>Seat Allocation:</strong> Create and manage your seating chart</li>
              </ul>
              
              <h3 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: '600' }}>
                How the +1 System Works
              </h3>
              
              <div style={{ marginTop: '1rem' }}>
                <p style={{ marginBottom: '0.75rem' }}>
                  <strong>When a guest has a +1:</strong>
                </p>
                <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
                  <li>Check the "Has +1" box when adding or editing a guest</li>
                  <li>Fill in the +1's details (first name, last name, email, phone)</li>
                  <li>In the list, you'll see the <strong>full name of the +1</strong> in the "+1" column</li>
                  <li>The +1 will appear as a separate guest in the list</li>
                </ul>
                
                <p style={{ marginBottom: '0.75rem' }}>
                  <strong>When a guest IS a +1:</strong>
                </p>
                <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
                  <li>In the "+1" column you'll see: <strong>"+1 (Main Guest's Name)"</strong></li>
                  <li>This helps you quickly identify who they're accompanying</li>
                  <li>The +1 maintains their own information (RSVP, dietary requirements, etc.)</li>
                </ul>
                
                <p style={{ marginBottom: '0.75rem', marginTop: '1rem' }}>
                  <strong>Example:</strong>
                </p>
                <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem', fontStyle: 'italic' }}>
                  <li>Main guest: "John Smith" → +1 Column: "Jane Smith"</li>
                  <li>+1 guest: "Jane Smith" → +1 Column: "+1 (John Smith)"</li>
                </ul>
                
                <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
                  <strong>Note:</strong> You can also add +1s from the public Lifesaver Link form. Guests can indicate if they're bringing a plus one when completing the form.
                </p>
              </div>
            </div>
          }
        />
      </h1>

      {/* Dashboard Section */}
      <div className="guest-dashboard-section">
        {/* Steps */}
        <div className="guest-steps">
          <Link to="/dashboard/guest-manager" className="guest-step-card">
            <span className="step-number">1</span>
            <span className="step-text">Guest list</span>
          </Link>
          <Link to="/dashboard/guest-manager/the-lifesaver-link" className="guest-step-card">
            <span className="step-number">2</span>
            <span className="step-text">The Lifesaver Link</span>
          </Link>
          <Link to="/dashboard/guest-manager/craft-your-invite" className="guest-step-card">
            <span className="step-number">3</span>
            <span className="step-text">Send an invite</span>
          </Link>
          <Link to="/dashboard/guest-manager/gentle-reminder-time" className="guest-step-card">
            <span className="step-number">4</span>
            <span className="step-text">RSVP Reminder</span>
          </Link>
          <Link to="/dashboard/guest-manager/who-sits-where" className="guest-step-card">
            <span className="step-number">5</span>
            <span className="step-text">Seat Allocation</span>
          </Link>
        </div>
      </div>

      {/* Guest List Section */}
      <div className="guest-list-section">
        <div className="guest-list-header">
          <h2 className="guest-list-title">Your official guest list</h2>
        </div>

        {/* Action Bar */}
        <div className="guest-action-bar">
          <div className="guest-action-left">
            <button className="guest-action-btn" onClick={() => showAlert('Import functionality coming soon')}>
              Import
            </button>
            <button className="guest-action-btn" onClick={() => showAlert('Export functionality coming soon')}>
              Export
            </button>
            <button className="guest-action-btn" onClick={() => showAlert('Print functionality coming soon')}>
              Print
            </button>
            <button className="guest-action-btn" onClick={() => showAlert('Highlight functionality coming soon')}>
              Highlight
            </button>
            <button className="guest-action-btn" onClick={() => showAlert('Collect details functionality coming soon')}>
              Collect details
            </button>
          </div>
          <div className="guest-action-right">
            <button className="guest-action-btn" onClick={() => setShowSettings(true)}>
              Settings
            </button>
            <button className="guest-action-btn guest-action-btn-primary" onClick={() => setShowAddGuest(true)}>
              Add guest
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="guest-tabs">
          <button
            className={`guest-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All ({stats.all})
          </button>
          <button
            className={`guest-tab ${activeTab === 'confirmed' ? 'active' : ''}`}
            onClick={() => setActiveTab('confirmed')}
          >
            Confirmed ({stats.confirmed})
          </button>
          <button
            className={`guest-tab ${activeTab === 'declined' ? 'active' : ''}`}
            onClick={() => setActiveTab('declined')}
          >
            Regretfully declined ({stats.declined})
          </button>
          <button
            className={`guest-tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Waiting for reply ({stats.pending})
          </button>
          <button
            className={`guest-tab ${activeTab === 'not_invited' ? 'active' : ''}`}
            onClick={() => setActiveTab('not_invited')}
          >
            Not invited yet ({stats.not_invited})
          </button>
        </div>

        {/* Filter and Search */}
        <div className="guest-filter-bar">
          <select
            className="guest-filter-select"
            value={filterBy}
            onChange={(e) => setFilterBy(e.target.value)}
          >
            <option value="">Filter by...</option>
            <option value="has_plus_one">Has +1</option>
            <option value="no_plus_one">No +1</option>
            {getUniqueGroups().map((group) => (
              <option key={group} value={`group_${group}`}>
                Group: {group}
              </option>
            ))}
            {getUniqueLists().map((list) => (
              <option key={list} value={`list_${list}`}>
                List: {list}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="guest-search-input"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Table */}
        <div className="guest-table-container">
          <table className="guest-table">
              <thead>
                <tr>
                  <th className="guest-table-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedGuests.length === filteredGuests.length && filteredGuests.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedGuests(filteredGuests.map((g) => g.id))
                        } else {
                          setSelectedGuests([])
                        }
                      }}
                    />
                  </th>
                  {visibleColumns.first_name && (
                    <th
                      className="guest-table-sortable"
                      onClick={() => handleSort('first_name')}
                    >
                      First name {sortBy === 'first_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.last_name && (
                    <th
                      className="guest-table-sortable"
                      onClick={() => handleSort('last_name')}
                    >
                      Last name {sortBy === 'last_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.plus_one && <th>+1</th>}
                  {visibleColumns.rsvp && <th>RSVP</th>}
                  {visibleColumns.email && <th>Email</th>}
                  {visibleColumns.list && <th>List</th>}
                  {visibleColumns.group && (
                    <th
                      className="guest-table-sortable"
                      onClick={() => handleSort('group')}
                    >
                      Group {sortBy === 'group' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.dietary && (
                    <th
                      className="guest-table-sortable"
                      onClick={() => handleSort('dietary')}
                    >
                      Dietary {sortBy === 'dietary' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.allergies && (
                    <th
                      className="guest-table-sortable"
                      onClick={() => handleSort('allergies')}
                    >
                      Allergies {sortBy === 'allergies' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.phone && <th>Phone</th>}
                  {visibleColumns.notes && <th>Notes</th>}
                  {customRsvpQuestions.map((q) => visibleColumns[`custom_${q.id}`] && (
                    <th key={q.id} title={getCustomColumnTitle(q)}>
                      {getCustomColumnLabel(q)}
                    </th>
                  ))}
                  {visibleColumns.table_number && <th>Table Number</th>}
                  {visibleColumns.position && <th>Position</th>}
                  <th className="guest-table-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGuests.length === 0 ? (
                  <tr>
                    <td colSpan="100%" className="guest-table-empty">
                      No guests found. Click "Add guest" to get started.
                    </td>
                  </tr>
                ) : (
                  filteredGuests.map((guest) => {
                    const rsvpDisplay = getRSVPStatusDisplay(guest.rsvp_status)
                    return (
                      <tr key={guest.id}>
                        <td className="guest-table-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedGuests.includes(guest.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedGuests([...selectedGuests, guest.id])
                              } else {
                                setSelectedGuests(selectedGuests.filter((id) => id !== guest.id))
                              }
                            }}
                          />
                        </td>
                        {visibleColumns.first_name && <td>{guest.first_name || '—'}</td>}
                        {visibleColumns.last_name && <td>{guest.last_name || '—'}</td>}
                        {visibleColumns.plus_one && (
                          <td>
                            {guest.has_plus_one ? (
                              // Si tiene +1, mostrar el nombre del +1 si está disponible
                              guest.plus_one_info ? (
                                <span title={`+1 of ${guest.first_name} ${guest.last_name}`}>
                                  {guest.plus_one_info.first_name} {guest.plus_one_info.last_name}
                                </span>
                              ) : (
                                // Si no se puede obtener el +1, mostrar indicador genérico
                                <span title={`+1 of ${guest.first_name} ${guest.last_name}`}>
                                  (+1)
                                </span>
                              )
                            ) : guest.guest_of_id ? (
                              // Si este invitado ES un +1, mostrar referencia al invitado principal
                              guest.main_guest_info ? (
                                <span style={{ opacity: 0.7, fontStyle: 'italic' }} title={`+1 of ${guest.main_guest_info.first_name} ${guest.main_guest_info.last_name}`}>
                                  +1 ({guest.main_guest_info.first_name} {guest.main_guest_info.last_name})
                                </span>
                              ) : (
                                <span style={{ opacity: 0.7, fontStyle: 'italic' }}>
                                  +1
                                </span>
                              )
                            ) : (
                              '—'
                            )}
                          </td>
                        )}
                        {visibleColumns.rsvp && (
                          <td>
                            <span
                              className="guest-rsvp-status"
                              style={{ color: rsvpDisplay.color }}
                            >
                              • {rsvpDisplay.text}
                            </span>
                          </td>
                        )}
                        {visibleColumns.email && <td>{guest.email || '—'}</td>}
                        {visibleColumns.list && (
                          <td>
                            {guest.guest_list ? (
                              <span className="guest-list-badge">{guest.guest_list}</span>
                            ) : (
                              '—'
                            )}
                          </td>
                        )}
                        {visibleColumns.group && <td>{guest.guest_group || '—'}</td>}
                        {visibleColumns.dietary && (
                          <td>
                            {guest.dietary_requirements && guest.dietary_requirements.trim() ? (
                              <span style={{ fontWeight: '500' }}>{guest.dietary_requirements}</span>
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>None</span>
                            )}
                          </td>
                        )}
                        {visibleColumns.allergies && (
                          <td>
                            {guest.allergies && guest.allergies.trim() ? (
                              <span style={{ fontWeight: '500', color: '#d32f2f' }}>{guest.allergies}</span>
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>None</span>
                            )}
                          </td>
                        )}
                        {visibleColumns.phone && <td>{guest.phone || '—'}</td>}
                        {visibleColumns.notes && (
                          <td className="guest-notes-cell" title={guest.notes || ''}>
                            {guest.notes && guest.notes.trim() ? (
                              <span className="guest-notes-text">{guest.notes.trim()}</span>
                            ) : (
                              '—'
                            )}
                          </td>
                        )}
                        {customRsvpQuestions.map((q) => visibleColumns[`custom_${q.id}`] && (
                          <td key={q.id} className="guest-notes-cell" title={formatCustomAnswer(guest.custom_rsvp_answers?.[q.id])}>
                            <span className="guest-notes-text">{formatCustomAnswer(guest.custom_rsvp_answers?.[q.id])}</span>
                          </td>
                        ))}
                        {visibleColumns.table_number && <td>{guest.table_number || '—'}</td>}
                        {visibleColumns.position && <td>{guest.position || '—'}</td>}
                        <td className="guest-table-actions">
                          <div className="guest-actions-buttons">
                            <button
                              className="guest-action-small guest-action-edit"
                              onClick={() => handleEditGuest(guest)}
                              title="Edit guest"
                            >
                              Edit
                            </button>
                            <button
                              className="guest-action-small"
                              onClick={() => handleDeleteGuest(guest.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div
          className="guest-settings-modal"
          onClick={async (e) => {
            if (e.target !== e.currentTarget) return
            const ok = await showConfirm('Leave without saving? Your column settings will not be saved.', { title: 'Discard changes?', confirmLabel: 'Leave', cancelLabel: 'Stay', variant: 'danger' })
            if (ok) setShowSettings(false)
          }}
        >
          <div className="guest-settings-content" onClick={(e) => e.stopPropagation()}>
            <div className="guest-settings-header">
              <h3>Guest settings</h3>
              <button className="guest-settings-close" onClick={() => setShowSettings(false)}>
                Cancel
              </button>
            </div>
            <p className="guest-settings-note">
              Note: Your dashboard may take longer to load if you display too many columns at once.
            </p>

            <div className="guest-settings-section">
              <h4>Basic details</h4>
              <p className="guest-settings-subtitle">Choose basic columns to display.</p>
              <div className="guest-settings-checkboxes">
                {[
                  { key: 'first_name', label: 'First name' },
                  { key: 'last_name', label: 'Last name' },
                  { key: 'plus_one', label: '+1' },
                  { key: 'rsvp', label: 'RSVP' },
                  { key: 'email', label: 'Email' },
                  { key: 'list', label: 'List' },
                  { key: 'group', label: 'Group' },
                  { key: 'dietary', label: 'Dietary' },
                  { key: 'allergies', label: 'Allergies' },
                  { key: 'phone', label: 'Phone' },
                  { key: 'notes', label: 'Notes' },
                  ...customRsvpQuestions.map((q) => ({ key: `custom_${q.id}`, label: getCustomColumnLabel(q), title: getCustomColumnTitle(q) })),
                  { key: 'table_number', label: 'Table Number' },
                  { key: 'position', label: 'Position' },
                ].map((col) => (
                  <label key={col.key} className="guest-settings-checkbox" title={col.title || null}>
                    <input
                      type="checkbox"
                      checked={visibleColumns[col.key] || false}
                      onChange={() => handleToggleColumn(col.key)}
                    />
                    <span>{col.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="guest-settings-footer">
              <button 
                type="button"
                className="guest-settings-save-btn"
                onClick={handleSaveColumnSettings}
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Guest Modal */}
      {showAddGuest && (
        <div
          className="guest-settings-modal"
          onClick={async (e) => {
            if (e.target !== e.currentTarget) return
            const ok = await showConfirm('Leave without saving? The guest will not be added.', { title: 'Discard changes?', confirmLabel: 'Leave', cancelLabel: 'Stay', variant: 'danger' })
            if (ok) {
              setShowAddGuest(false)
              setNewGuest({
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                rsvp_status: 'pending',
                has_plus_one: false,
                plus_one_first_name: '',
                plus_one_last_name: '',
                plus_one_email: '',
                plus_one_phone: '',
                dietary_requirements: '',
                allergies: '',
                guest_list: '',
                guest_group: '',
                table_number: '',
                position: '',
                notes: '',
              })
            }
          }}
        >
          <div className="guest-settings-content guest-add-content" onClick={(e) => e.stopPropagation()}>
            <div className="guest-settings-header">
              <h3>Add Guest</h3>
              <button className="guest-settings-close" onClick={() => setShowAddGuest(false)}>
                Cancel
              </button>
            </div>
            <form onSubmit={handleAddGuest} className="guest-add-form">
              <div className="guest-add-form-row">
                <div className="guest-add-form-field">
                  <label>First name *</label>
                  <input
                    type="text"
                    value={newGuest.first_name}
                    onChange={(e) => setNewGuest({ ...newGuest, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="guest-add-form-field">
                  <label>Last name *</label>
                  <input
                    type="text"
                    value={newGuest.last_name}
                    onChange={(e) => setNewGuest({ ...newGuest, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="guest-add-form-row">
                <div className="guest-add-form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newGuest.email}
                    onChange={(e) => setNewGuest({ ...newGuest, email: e.target.value })}
                  />
                </div>
                <div className="guest-add-form-field">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newGuest.phone}
                    onChange={(e) => setNewGuest({ ...newGuest, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="guest-add-form-row">
                <div className="guest-add-form-field">
                  <label>RSVP Status</label>
                  <select
                    value={newGuest.rsvp_status}
                    onChange={(e) => setNewGuest({ ...newGuest, rsvp_status: e.target.value })}
                  >
                    <option value="pending">Waiting for reply</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="declined">Regretfully declined</option>
                    <option value="not_invited">Not invited yet</option>
                  </select>
                </div>
                <div className="guest-add-form-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={newGuest.has_plus_one}
                      onChange={(e) => setNewGuest({ ...newGuest, has_plus_one: e.target.checked })}
                    />
                    Has +1
                  </label>
                </div>
              </div>
              {newGuest.has_plus_one && (
                <div className="guest-add-form-plus-one-section">
                  <h4>Plus One Details</h4>
                  <div className="guest-add-form-row">
                    <div className="guest-add-form-field">
                      <label>Plus One First name *</label>
                      <input
                        type="text"
                        value={newGuest.plus_one_first_name}
                        onChange={(e) => setNewGuest({ ...newGuest, plus_one_first_name: e.target.value })}
                        required={newGuest.has_plus_one}
                      />
                    </div>
                    <div className="guest-add-form-field">
                      <label>Plus One Last name *</label>
                      <input
                        type="text"
                        value={newGuest.plus_one_last_name}
                        onChange={(e) => setNewGuest({ ...newGuest, plus_one_last_name: e.target.value })}
                        required={newGuest.has_plus_one}
                      />
                    </div>
                  </div>
                  <div className="guest-add-form-row">
                    <div className="guest-add-form-field">
                      <label>Plus One Email</label>
                      <input
                        type="email"
                        value={newGuest.plus_one_email}
                        onChange={(e) => setNewGuest({ ...newGuest, plus_one_email: e.target.value })}
                      />
                    </div>
                    <div className="guest-add-form-field">
                      <label>Plus One Phone</label>
                      <input
                        type="tel"
                        value={newGuest.plus_one_phone}
                        onChange={(e) => setNewGuest({ ...newGuest, plus_one_phone: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="guest-add-form-row">
                <div className="guest-add-form-field">
                  <label>Group</label>
                  <input
                    type="text"
                    value={newGuest.guest_group}
                    onChange={(e) => setNewGuest({ ...newGuest, guest_group: e.target.value })}
                    placeholder="e.g., Family, Friends, Work, College"
                  />
                </div>
              </div>
              <div className="guest-add-form-row">
                <div className="guest-add-form-field">
                  <label>Dietary requirements</label>
                  <input
                    type="text"
                    value={newGuest.dietary_requirements}
                    onChange={(e) => setNewGuest({ ...newGuest, dietary_requirements: e.target.value })}
                    placeholder="e.g., Vegan, Vegetarian"
                  />
                </div>
                <div className="guest-add-form-field">
                  <label>Allergies</label>
                  <input
                    type="text"
                    value={newGuest.allergies}
                    onChange={(e) => setNewGuest({ ...newGuest, allergies: e.target.value })}
                    placeholder="e.g., Nut allergies, Shellfish, Dairy"
                  />
                </div>
                <div className="guest-add-form-field">
                  <label>List</label>
                  <input
                    type="text"
                    value={newGuest.guest_list}
                    onChange={(e) => setNewGuest({ ...newGuest, guest_list: e.target.value })}
                    placeholder="e.g., A, B"
                  />
                </div>
              </div>
              <div className="guest-add-form-row">
                <div className="guest-add-form-field">
                  <label>Table Number</label>
                  <input
                    type="number"
                    value={newGuest.table_number}
                    onChange={(e) => setNewGuest({ ...newGuest, table_number: e.target.value })}
                  />
                </div>
                <div className="guest-add-form-field">
                  <label>Position</label>
                  <input
                    type="text"
                    value={newGuest.position}
                    onChange={(e) => setNewGuest({ ...newGuest, position: e.target.value })}
                  />
                </div>
              </div>
              <div className="guest-add-form-field">
                <label>Notes</label>
                <TextareaWithCounter
                  value={newGuest.notes}
                  onChange={(e) => setNewGuest({ ...newGuest, notes: e.target.value })}
                  rows={3}
                  maxLength={500}
                />
              </div>
              <div className="guest-add-form-actions">
                <button 
                  type="button" 
                  onClick={() => setShowAddGuest(false)}
                  disabled={isAddingGuest}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="guest-action-btn-primary"
                  disabled={isAddingGuest}
                >
                  {isAddingGuest ? 'Adding...' : 'Add Guest'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Guest Modal */}
      {editingGuest && (
        <div
          className="guest-settings-modal"
          onClick={async (e) => {
            if (e.target !== e.currentTarget) return
            const ok = await showConfirm('Leave without saving? Your changes to this guest will not be saved.', { title: 'Discard changes?', confirmLabel: 'Leave', cancelLabel: 'Stay', variant: 'danger' })
            if (ok) {
              setEditingGuest(null)
              setEditGuestData({})
            }
          }}
        >
          <div className="guest-settings-content guest-add-content" onClick={(e) => e.stopPropagation()}>
            <div className="guest-settings-header">
              <h3>Edit Guest</h3>
              <button className="guest-settings-close" onClick={() => {
                setEditingGuest(null)
                setEditGuestData({})
              }}>
                Cancel
              </button>
            </div>
            <form onSubmit={handleSaveEditGuest} className="guest-add-form">
              <div className="guest-add-form-row">
                <div className="guest-add-form-field">
                  <label>First name *</label>
                  <input
                    type="text"
                    value={editGuestData.first_name}
                    onChange={(e) => setEditGuestData({ ...editGuestData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="guest-add-form-field">
                  <label>Last name *</label>
                  <input
                    type="text"
                    value={editGuestData.last_name}
                    onChange={(e) => setEditGuestData({ ...editGuestData, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="guest-add-form-row">
                <div className="guest-add-form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editGuestData.email}
                    onChange={(e) => setEditGuestData({ ...editGuestData, email: e.target.value })}
                  />
                </div>
                <div className="guest-add-form-field">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editGuestData.phone}
                    onChange={(e) => setEditGuestData({ ...editGuestData, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="guest-add-form-row">
                <div className="guest-add-form-field">
                  <label>RSVP Status</label>
                  <select
                    value={editGuestData.rsvp_status}
                    onChange={(e) => setEditGuestData({ ...editGuestData, rsvp_status: e.target.value })}
                  >
                    <option value="pending">Waiting for reply</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="declined">Regretfully declined</option>
                    <option value="not_invited">Not invited yet</option>
                  </select>
                </div>
                <div className="guest-add-form-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={editGuestData.has_plus_one}
                      onChange={(e) => setEditGuestData({ ...editGuestData, has_plus_one: e.target.checked })}
                    />
                    Has +1
                  </label>
                </div>
              </div>
              {editGuestData.has_plus_one && (
                <div className="guest-add-form-plus-one-section">
                  <h4>Plus One Details</h4>
                  <div className="guest-add-form-row">
                    <div className="guest-add-form-field">
                      <label>Plus One First name *</label>
                      <input
                        type="text"
                        value={editGuestData.plus_one_first_name}
                        onChange={(e) => setEditGuestData({ ...editGuestData, plus_one_first_name: e.target.value })}
                        required={editGuestData.has_plus_one}
                      />
                    </div>
                    <div className="guest-add-form-field">
                      <label>Plus One Last name *</label>
                      <input
                        type="text"
                        value={editGuestData.plus_one_last_name}
                        onChange={(e) => setEditGuestData({ ...editGuestData, plus_one_last_name: e.target.value })}
                        required={editGuestData.has_plus_one}
                      />
                    </div>
                  </div>
                  <div className="guest-add-form-row">
                    <div className="guest-add-form-field">
                      <label>Plus One Email</label>
                      <input
                        type="email"
                        value={editGuestData.plus_one_email}
                        onChange={(e) => setEditGuestData({ ...editGuestData, plus_one_email: e.target.value })}
                      />
                    </div>
                    <div className="guest-add-form-field">
                      <label>Plus One Phone</label>
                      <input
                        type="tel"
                        value={editGuestData.plus_one_phone}
                        onChange={(e) => setEditGuestData({ ...editGuestData, plus_one_phone: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="guest-add-form-row">
                <div className="guest-add-form-field">
                  <label>Group</label>
                  <input
                    type="text"
                    value={editGuestData.guest_group}
                    onChange={(e) => setEditGuestData({ ...editGuestData, guest_group: e.target.value })}
                    placeholder="e.g., Family, Friends, Work, College"
                  />
                </div>
              </div>
              <div className="guest-add-form-row">
                <div className="guest-add-form-field">
                  <label>Dietary requirements</label>
                  <input
                    type="text"
                    value={editGuestData.dietary_requirements}
                    onChange={(e) => setEditGuestData({ ...editGuestData, dietary_requirements: e.target.value })}
                    placeholder="e.g., Vegan, Vegetarian"
                  />
                </div>
                <div className="guest-add-form-field">
                  <label>Allergies</label>
                  <input
                    type="text"
                    value={editGuestData.allergies}
                    onChange={(e) => setEditGuestData({ ...editGuestData, allergies: e.target.value })}
                    placeholder="e.g., Nut allergies, Shellfish, Dairy"
                  />
                </div>
                <div className="guest-add-form-field">
                  <label>List</label>
                  <input
                    type="text"
                    value={editGuestData.guest_list}
                    onChange={(e) => setEditGuestData({ ...editGuestData, guest_list: e.target.value })}
                    placeholder="e.g., A, B"
                  />
                </div>
              </div>
              <div className="guest-add-form-row">
                <div className="guest-add-form-field">
                  <label>Table Number</label>
                  <input
                    type="number"
                    value={editGuestData.table_number}
                    onChange={(e) => setEditGuestData({ ...editGuestData, table_number: e.target.value })}
                  />
                </div>
                <div className="guest-add-form-field">
                  <label>Position</label>
                  <input
                    type="text"
                    value={editGuestData.position}
                    onChange={(e) => setEditGuestData({ ...editGuestData, position: e.target.value })}
                  />
                </div>
              </div>
              <div className="guest-add-form-field">
                <label>Notes</label>
                <TextareaWithCounter
                  value={editGuestData.notes}
                  onChange={(e) => setEditGuestData({ ...editGuestData, notes: e.target.value })}
                  rows={3}
                  maxLength={500}
                />
              </div>
              <div className="guest-add-form-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setEditingGuest(null)
                    setEditGuestData({})
                  }}
                  disabled={isUpdatingGuest}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="guest-action-btn-primary"
                  disabled={isUpdatingGuest}
                >
                  {isUpdatingGuest ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default GuestManagerIndex
